import React from "react";
import { useNavigate } from "react-router-dom";
import { Activity, FileCheck2, Gavel, MessageSquareText, BookOpen, Building2, Mail, ArrowRight } from "lucide-react";
import { useI18n } from "../../i18n";
import Navbar from "../../components/marketing/Navbar";
import Footer from "../../components/marketing/Footer";
import { Button } from "../../components/ui/button";

const CONTENT = {
  how: {
    icon: Activity, title: "nav_how",
    intro: { en: "Every request becomes a governed professional engagement: Client Submission → Document Intake → Automated Screening → VAT Specialist Review → Supervisor Review → Quality Review → Approved Client Deliverable.", ar: "كل طلب يتحوّل إلى ارتباط مهني منضبط: طلب العميل ← استلام المستندات ← الفحص الآلي ← مراجعة المختص ← مراجعة المشرف ← مراجعة الجودة ← مخرج معتمد للعميل." },
    points: [["step_ai", "trust_body"], ["step_specialist", "feat_return_d"], ["step_quality", "feat_risk_d"]],
  },
  health: {
    icon: Activity, title: "nav_health",
    intro: { en: "A monthly VAT Health Check with automated analytics and specialist review across 13 categories — duplicate invoices, missing VAT numbers, input/output mismatches, reconciliation variances and more.", ar: "فحص شهري لصحة الضريبة بتحليلات آلية ومراجعة المختص عبر 13 فئة — الفواتير المكررة، أرقام الضريبة الناقصة، عدم تطابق المدخلات/المخرجات، فروقات التسوية والمزيد." },
    points: [["feat_health_t", "feat_health_d"], ["vat_health_score", "diag_disclaimer"]],
  },
  compliance: {
    icon: FileCheck2, title: "nav_compliance",
    intro: { en: "VAT Return Review with preliminary reconciliation, specialist review of taxable/zero-rated/exempt supplies, imports, reverse charge and input VAT — then supervisor and quality approval before you approve for filing.", ar: "مراجعة الإقرار الضريبي مع تسوية أولية ومراجعة المختص للتوريدات الخاضعة والصفرية والمعفاة والواردات والاحتساب العكسي وضريبة المدخلات — ثم اعتماد المشرف والجودة قبل اعتمادك للتقديم." },
    points: [["feat_return_t", "feat_return_d"]],
  },
  advisory: {
    icon: MessageSquareText, title: "nav_advisory",
    intro: { en: "Ask a VAT Specialist. A preliminary analysis is drafted automatically — facts understood, missing facts, potential treatment, relevant regulations and risk considerations — then reviewed by a specialist into a final VAT Advisory Note.", ar: "اسأل مختص ضريبي. يُصاغ تحليل أولي تلقائياً — الحقائق المفهومة والناقصة والمعالجة المحتملة والأنظمة ذات الصلة واعتبارات المخاطر — ثم يراجعه المختص إلى مذكرة استشارية نهائية." },
    points: [["feat_advisory_t", "feat_advisory_d"]],
  },
  zatca: {
    icon: Gavel, title: "nav_zatca",
    intro: { en: "ZATCA Audit Support: upload a notice, the system extracts type, reference, deadline and requested documents, then a full workflow — issue matrix, evidence mapping, response drafting, supervisor and quality review — to a final response package.", ar: "دعم فحص الهيئة: ارفع الإشعار، يستخرج النظام تلقائياً النوع والمرجع والموعد والمستندات المطلوبة، ثم سير عمل كامل — مصفوفة القضايا وربط الأدلة وصياغة الرد ومراجعة المشرف والجودة — حتى حزمة الرد النهائية." },
    points: [["feat_zatca_t", "feat_zatca_d"]],
  },
  knowledge: {
    icon: BookOpen, title: "nav_knowledge",
    intro: { en: "A professional VAT Knowledge Engine: Saudi VAT Law, Implementing Regulations, ZATCA guidance, rulings, GSTC decisions and Growth Curve internal interpretations — with a clear source hierarchy. We never invent a legal source.", ar: "محرك معرفة ضريبي مهني: نظام ضريبة القيمة المضافة السعودي واللائحة التنفيذية وإرشادات الهيئة والقرارات وقرارات الأمانة العامة للجان الضريبية وتفسيرات Growth Curve الداخلية — بتسلسل واضح للمصادر. ولا يُختلق أي مصدر قانوني." },
    points: [],
  },
  about: {
    icon: Building2, title: "nav_about",
    intro: { en: "VATRdar is a product of Growth Curve Professional Consultancy. All intellectual property, content, workflows, methodology and outputs are owned by Growth Curve. A professional VAT specialist team leads every engagement, supported by smart automation, supervisor approval and quality control.", ar: "VATRdar منتج من Growth Curve للاستشارات المهنية. جميع الملكية الفكرية والمحتوى وسير العمل والمنهجية والمخرجات مملوكة لـ Growth Curve. يقود كل ارتباط فريق مختصين في ضريبة القيمة المضافة، بدعم من الأتمتة الذكية واعتماد المشرف ورقابة الجودة." },
    points: [],
  },
  contact: {
    icon: Mail, title: "nav_contact",
    intro: { en: "Talk to our VAT team about a subscription or a professional engagement. Start with a free VAT diagnostic and we will recommend the right plan for your business.", ar: "تحدّث مع فريقنا الضريبي حول اشتراك أو ارتباط مهني. ابدأ بتشخيص ضريبي مجاني وسنوصي بالباقة المناسبة لنشاطك." },
    points: [],
  },
};

export default function Marketing({ page }) {
  const { t, lang, dir } = useI18n();
  const nav = useNavigate();
  const c = CONTENT[page] || CONTENT.how;
  const Icon = c.icon;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="gc-gradient-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center">
            <Icon className="h-6 w-6" style={{ color: "hsl(41 72% 60%)" }} />
          </div>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t(c.title)}</h1>
          <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-3xl">{c.intro[lang]}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-5">
          {c.points.map(([tk, dk], i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-6">
              <div className="font-mono text-xs text-[hsl(158_64%_28%)]">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-2 font-semibold text-lg text-[hsl(217_63%_15%)]">{t(tk)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(dk)}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-2xl border border-border bg-secondary/40 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <div className="font-display text-xl font-bold text-[hsl(217_63%_15%)]">{t("cta_check")}</div>
            <div className="text-sm text-muted-foreground mt-1">{t("diag_sub")}</div>
          </div>
          <div className="flex gap-3">
            <Button data-testid="mk-diag-btn" onClick={() => nav("/diagnostic")} className="rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">
              {t("cta_check")} <ArrowRight className="ms-2 h-4 w-4" style={{ transform: dir === "rtl" ? "scaleX(-1)" : "none" }} />
            </Button>
            <Button data-testid="mk-plans-btn" variant="outline" onClick={() => nav("/pricing")} className="rounded-full">{t("cta_plans")}</Button>
          </div>
        </div>
        <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer_note")}</p>
      </section>
      <Footer />
    </div>
  );
}
